import React, { useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { OverviewModuleData } from '../../types/ModuleData'
import { useSelector } from '../store/hooks'
import { useInspirationDetail } from '../store/hooks/uiHooks'
import { ANIMATION_SCALE, breakpoint, device } from '../style'
import { useTooltip } from '../utils/useTooltip'
import CloneDraggable from './dnd/CloneDraggable'
import getDragType from './dnd/getDragType'
import Icon from './Icon'
import PreviewModule, { useDefaultModuleScale } from './preview/PreviewModule'
import { useTranslation } from '../store/hooks/dataHooks'

export type ModuleButtonProps = {
  index: number
  module: OverviewModuleData
  className?: string
}

export const Thumbnail = styled.div<{ src: string }>`
  position: absolute;
  top: 5px;
  left: 5px;
  right: 5px;
  bottom: 5px;
  background: no-repeat center/contain url('${(props) => props.src}');
`

export const ThumbnailWrap = styled.div<{ smaller?: boolean }>`
  width: 118px;
  height: 118px;
  position: relative;

  ${({ smaller }) =>
    smaller
      ? css`
          ${Thumbnail} {
            top: 10px;
            left: 10px;
            right: 10px;
            bottom: 10px;
          }
        `
      : ''}
`

export const DragHandler = styled.div.attrs({
  className: 'button button--solid',
  children: <Icon name="move-square" size={20} />
})`
  cursor: grab;
  width: 49px;
  height: 49px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
  position: relative;
  background-color: #fff;
  color: #000;
`

const ModuleButton = styled<React.FC<ModuleButtonProps>>(({ className, module, index }) => {
  const { showDetail } = useInspirationDetail()
  const onClick = useCallback(() => showDetail(module.id), [module.id, showDetail])
  const { moduleScale } = useDefaultModuleScale()
  const screenWidth = useSelector((state) => state.ui.screenSize.width)
  const t = useTranslation()
  const delay = (1.55 + index * 0.1) * ANIMATION_SCALE * 1000
  const [enabled, setEnabled] = useState(!delay)
  useEffect(() => {
    if (!delay) {
      setEnabled(true)
    } else {
      const timeout = setTimeout(() => setEnabled(true), delay)
      return () => clearTimeout(timeout)
    }
  }, [delay])

  const tooltip = useTooltip(
    !enabled ? undefined : module.type === 'Service' ? t('ui.tooltip.drag.service') : t('ui.tooltip.drag.modul')
  )

  return (
    <div className={className} data-cs-ignore-close="moduleDetail" style={{ pointerEvents: enabled ? 'all' : 'none' }}>
      <button onClick={onClick} ref={tooltip}>
        <CloneDraggable
          enabled={screenWidth >= breakpoint.tablet}
          draggableId={module.id}
          type={getDragType(module)}
          renderClone={() => <PreviewModule module={module} scale={moduleScale} />}
        >
          <ThumbnailWrap smaller={module.type === 'Service'}>
            <Thumbnail src={(module.thumbnail ?? module.preview).url} />
            {module.thumbnailHover && <Thumbnail src={module.thumbnailHover.url} />}
          </ThumbnailWrap>
          <div>{module.title}</div>
          {/* {screenWidth >= breakpoint.tablet && <DragHandler />} */}
        </CloneDraggable>
      </button>
    </div>
  )
})<ModuleButtonProps>`
  flex: 1;
  min-width: 160px;
  max-width: 160px;
  height: 160px;
  padding: 0 5px;
  position: relative;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  @media ${device.laptop} {
    min-width: 170px;
    max-width: 170px;
    height: 170px;
    padding: 5px !important;
  }

  button:hover,
  button:active {
    border-color: #cf0000;
    background-color: #fff;
    color: #000;
  }

  button {
    width: 100%;
    height: 100%;
    user-style: none;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid transparent;
    border-radius: 5px;
    cursor: pointer;
    color: #fff;
    font-weight: bold;
    text-align: center;
    font-size: 12px;
    line-height: 1.2;

    ${CloneDraggable} {
      padding: 10px;
    }

    @media ${device.tablet} {
      font-size: 14px;
    }

    transition: all ${0.2 * ANIMATION_SCALE}s ease-in-out;
    transition-property: border-color, color, background-color;

    &:active {
      border-color: #cf0000;
      color: #000;
      background-color: #f2f2f2;
      transition-duration: 0s;
    }
  }

  transition-delay: ${({ index }) => (1 + index * 0.1) * ANIMATION_SCALE}s;

  ${ThumbnailWrap} {
    margin: auto;
    margin-top: 7.5px;
    margin-bottom: 10px;
  }

  ${DragHandler} {
    position: absolute;
    top: 5px;
    right: 5px;
  }

  ${CloneDraggable} {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
  }

  ${({ module }) =>
    !!module.thumbnailHover
      ? css`
          ${ThumbnailWrap} > * {
            transition: opacity ${0.3 * ANIMATION_SCALE}s;
          }

          ${ThumbnailWrap} > :nth-child(2) {
            opacity: 0;
          }

          &:hover,
          &:active {
            ${ThumbnailWrap} > :nth-child(1) {
              opacity: 0;
            }
            ${ThumbnailWrap} > :nth-child(2) {
              opacity: 1;
            }
          }
        `
      : ''}
`

export default ModuleButton
